import React, { useEffect } from "react";
import DOMPurify from "dompurify";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  CircularProgress,
  Chip,
  Divider,
  Typography,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import EventIcon from "@material-ui/icons/Event";
import PersonIcon from "@material-ui/icons/Person";
import AssignmentIcon from "@material-ui/icons/Assignment";
import LocalHospitalIcon from "@material-ui/icons/LocalHospital";
import FaceIcon from "@material-ui/icons/Face";
import useBDocTranslator from "../hooks/useBDocTranslator";
import useUserState from "../hooks/useUserState";
import { useGetPrescriptionById } from "../../services/services";
import { getPlaceTypeLabel } from "../../utils/prescriptionPlace";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: theme.shadows[10],
    padding: theme.spacing(3, 4, 4),
    width: "720px",
    maxWidth: "95vw",
    maxHeight: "90vh",
    overflowY: "auto",
    outline: "none",
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1.5),
  },
  headerIcon: {
    color: "#01579b",
    fontSize: 36,
  },
  title: {
    color: "#01579b",
    fontWeight: 700,
    fontSize: "1.3rem",
  },
  section: {
    backgroundColor: "#f5f8fc",
    borderRadius: "6px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.8),
    color: "#01579b",
    fontWeight: 600,
    fontSize: "0.9rem",
    marginBottom: theme.spacing(1.5),
  },
  sectionIcon: {
    fontSize: 18,
    color: "#01579b",
  },
  fieldLabel: {
    color: "#78909c",
    fontSize: "0.78rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "2px",
  },
  fieldValue: {
    color: "#263238",
    fontSize: "0.95rem",
    wordBreak: "break-word",
  },
  directionsList: {
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    padding: theme.spacing(0.5, 1),
  },
  directionItem: {
    paddingTop: "4px",
    paddingBottom: "4px",
    borderBottom: "1px solid #f0f0f0",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  chip: {
    backgroundColor: "#e3f2fd",
    color: "#01579b",
    fontWeight: 600,
    fontSize: "0.8rem",
  },
  draftChip: {
    backgroundColor: "#fff3e0",
    color: "#e65100",
    fontWeight: 600,
    fontSize: "0.8rem",
    marginLeft: theme.spacing(1),
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  errorText: {
    color: "#c62828",
    textAlign: "center",
    padding: theme.spacing(3),
  },
  actionsRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  downloadBtn: {
    backgroundColor: "#01579b",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#013f6e",
    },
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  idBadge: {
    backgroundColor: "#e3f2fd",
    color: "#01579b",
    fontWeight: 700,
    borderRadius: "4px",
    padding: "2px 8px",
    fontSize: "0.85rem",
    display: "inline-block",
    marginRight: theme.spacing(1),
  },
  headerBadges: {
    display: "flex",
    alignItems: "center",
    marginTop: "4px",
  },
  directionItemText: {
    fontSize: "0.92rem",
    color: "#263238",
  },
  emptyStateText: {
    color: "#90a4ae",
    fontStyle: "italic",
    fontSize: "0.9rem",
  },
}));

export default function PrescriptionDetailModal({
  open,
  handleClose,
  prescriptionId,
}) {
  const classes = useStyles();
  const { t } = useBDocTranslator();
  const userLocalState = useUserState();
  const { loading, error, prescriptionData, getPrescriptionById } =
    useGetPrescriptionById();

  useEffect(() => {
    if (open && prescriptionId) {
      getPrescriptionById(prescriptionId, userLocalState[0]?.jwt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prescriptionId]);

  const handleDownload = () => {
    if (prescriptionData?.url_prescription) {
      window.open(prescriptionData.url_prescription, "_blank");
    }
  };

  const isDraft =
    prescriptionData?.is_draft === "true" ||
    prescriptionData?.is_draft === true;

  const patientDirections =
    prescriptionData?.patient_directions &&
    typeof prescriptionData.patient_directions === "string" &&
    prescriptionData.patient_directions.trim() !== ""
      ? prescriptionData.patient_directions
      : null;

  // The backend may not send the place yet (the front can be deployed first),
  // so the whole section is optional.
  const place = prescriptionData?.place || null;
  const placeTypeLabel = place ? getPlaceTypeLabel(place.type, t) : null;
  const agreementCode = place?.agreement_code || null;

  return (
    <Modal
      aria-labelledby="prescription-detail-title"
      aria-describedby="prescription-detail-description"
      className={classes.modal}
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          {/* Header */}
          <div className={classes.header}>
            <AssignmentIcon className={classes.headerIcon} />
            <div>
              <Typography className={classes.title}>
                {t("Prescription Details")}
              </Typography>
              {prescriptionData && (
                <div className={classes.headerBadges}>
                  <span className={classes.idBadge}>
                    #{prescriptionData.id}
                  </span>
                  {prescriptionData.event_code && (
                    <Chip
                      size="small"
                      label={prescriptionData.event_code}
                      className={classes.chip}
                    />
                  )}
                  {isDraft && (
                    <Chip
                      size="small"
                      label={t("Draft")}
                      className={classes.draftChip}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          <Divider className={classes.divider} />

          {/* Loading state */}
          {loading && (
            <div className={classes.loaderContainer}>
              <CircularProgress style={{ color: "#01579b" }} />
            </div>
          )}

          {/* Error state */}
          {!loading && error && (
            <Typography className={classes.errorText}>
              {t("Error loading prescription details. Please try again.")}
            </Typography>
          )}

          {/* Content */}
          {!loading && !error && prescriptionData && (
            <>
              {/* Provider Information */}
              {prescriptionData.provider && (
                <div className={classes.section}>
                  <div className={classes.sectionTitle}>
                    <LocalHospitalIcon className={classes.sectionIcon} />
                    {t("Provider Information")}
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <Typography className={classes.fieldLabel}>
                        {t("Name")}
                      </Typography>
                      <Typography className={classes.fieldValue}>
                        {prescriptionData.provider.name || "-"}
                      </Typography>
                    </div>
                    <div className="col-md-6 mb-2">
                      <Typography className={classes.fieldLabel}>
                        {t("Specialty")}
                      </Typography>
                      <Typography className={classes.fieldValue}>
                        {prescriptionData.provider.speciality || "-"}
                      </Typography>
                    </div>
                    <div className="col-md-6 mb-2">
                      <Typography className={classes.fieldLabel}>
                        {t("Medical License")}
                      </Typography>
                      <Typography className={classes.fieldValue}>
                        {prescriptionData.provider.medical_license || "-"}
                      </Typography>
                    </div>
                    <div className="col-md-6 mb-2">
                      <Typography className={classes.fieldLabel}>
                        {t("Email")}
                      </Typography>
                      <Typography className={classes.fieldValue}>
                        {prescriptionData.provider.email || "-"}
                      </Typography>
                    </div>
                    <div className="col-md-6 mb-2">
                      <Typography className={classes.fieldLabel}>
                        {t("Phone")}
                      </Typography>
                      <Typography className={classes.fieldValue}>
                        {prescriptionData.provider.phone || "-"}
                      </Typography>
                    </div>

                  </div>
                </div>
              )}

              {/* Patient Information */}
              {prescriptionData.patient && (
                <div className={classes.section}>
                  <div className={classes.sectionTitle}>
                    <FaceIcon className={classes.sectionIcon} />
                    {t("Patient Information")}
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <Typography className={classes.fieldLabel}>
                        {t("Name")}
                      </Typography>
                      <Typography className={classes.fieldValue}>
                        {prescriptionData.patient.name || "-"}
                      </Typography>
                    </div>
                    <div className="col-md-6 mb-2">
                      <Typography className={classes.fieldLabel}>
                        {t("Identification")}
                      </Typography>
                      <Typography className={classes.fieldValue}>
                        {prescriptionData.patient.identification || "-"}
                      </Typography>
                    </div>
                    <div className="col-md-6 mb-2">
                      <Typography className={classes.fieldLabel}>
                        {t("Email")}
                      </Typography>
                      <Typography className={classes.fieldValue}>
                        {prescriptionData.patient.email || "-"}
                      </Typography>
                    </div>
                    <div className="col-md-6 mb-2">
                      <Typography className={classes.fieldLabel}>
                        {t("Phone")}
                      </Typography>
                      <Typography className={classes.fieldValue}>
                        {prescriptionData.patient.phone || "-"}
                      </Typography>
                    </div>
                    <div className="col-md-6 mb-2">
                      <Typography className={classes.fieldLabel}>
                        {t("Insurance Provider")}
                      </Typography>
                      <Typography className={classes.fieldValue}>
                        {prescriptionData.patient.insurance_provider || "-"}
                      </Typography>
                    </div>
                    <div className="col-md-6 mb-2">
                      <Typography className={classes.fieldLabel}>
                        {t("Insurance Policy")}
                      </Typography>
                      <Typography className={classes.fieldValue}>
                        {prescriptionData.patient.insurance_policy_number || "-"}
                      </Typography>
                    </div>
                  </div>
                </div>
              )}

              {/* Place */}
              {place && (
                <div className={classes.section}>
                  <div className={classes.sectionTitle}>
                    <LocalHospitalIcon className={classes.sectionIcon} />
                    {t("Place")}
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <Typography className={classes.fieldLabel}>
                        {t("Name")}
                      </Typography>
                      <Typography className={classes.fieldValue}>
                        {place.name || "-"}
                      </Typography>
                    </div>
                    <div className="col-md-6 mb-2">
                      <Typography className={classes.fieldLabel}>
                        {t("Type")}
                      </Typography>
                      <Typography className={classes.fieldValue}>
                        {placeTypeLabel || "-"}
                      </Typography>
                    </div>
                    <div className="col-md-6 mb-2">
                      <Typography className={classes.fieldLabel}>
                        {t("City")}
                      </Typography>
                      <Typography className={classes.fieldValue}>
                        {place.city || "-"}
                      </Typography>
                    </div>
                    {agreementCode && (
                      <div className="col-md-6 mb-2">
                        <Typography className={classes.fieldLabel}>
                          {t("Agreement Code")}
                        </Typography>
                        <Chip
                          size="small"
                          label={agreementCode}
                          className={classes.chip}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className={classes.section}>
                <div className={classes.sectionTitle}>
                  <EventIcon className={classes.sectionIcon} />
                  {t("Dates")}
                </div>
                <div className="row">
                  <div className="col-md-6 mb-2">
                    <Typography className={classes.fieldLabel}>
                      {t("Valid from")}
                    </Typography>
                    <Typography className={classes.fieldValue}>
                      {prescriptionData.dispense_from || "-"}
                    </Typography>
                  </div>
                  <div className="col-md-6 mb-2">
                    <Typography className={classes.fieldLabel}>
                      {t("Valid until")}
                    </Typography>
                    <Typography className={classes.fieldValue}>
                      {prescriptionData.dispense_to || "-"}
                    </Typography>
                  </div>
                </div>
              </div>

              {/* Patient Directions */}
              <div className={classes.section}>
                <div className={classes.sectionTitle}>
                  <PersonIcon className={classes.sectionIcon} />
                  {t("Patient Directions")}
                </div>
                {patientDirections ? (
                  <Typography
                    className={classes.fieldValue}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(patientDirections) }}
                  />
                ) : (
                  <Typography className={classes.emptyStateText}>
                    {t("No directions provided")}
                  </Typography>
                )}
              </div>
            </>
          )}

          {/* Actions */}
          <div className={classes.actionsRow}>
            {!loading && prescriptionData?.url_prescription && (
              <Button
                variant="contained"
                className={classes.downloadBtn}
                startIcon={<GetAppIcon />}
                onClick={handleDownload}
              >
                {t("Download")}
              </Button>
            )}
            <Button variant="outlined" color="default" onClick={handleClose}>
              {t("Close")}
            </Button>
          </div>
        </div>
      </Fade>
    </Modal>
  );
}
